import { createConfig, fallback, http } from "wagmi";
import { injected, mock } from "wagmi/connectors";
import { arbitrum, base, bsc, ethereum, hyperevm, robinhood } from "./config/chains";

/**
 * `?mirar=0x…` — MIRAR la app como si esa wallet estuviera conectada, SIN firmar nada.
 *
 * Las puertas `?barra=1` y `?demo=…` solo llegan hasta donde llega una pantalla sin datos: la lista
 * de vaults y la ficha —las dos mas densas, y las que peor caen en un movil— no se pueden revisar
 * sin cuenta, y ahi es donde se rompen las cosas. Con esto se abren en cualquier ancho.
 *
 * Es de SOLO LECTURA por construccion: el conector `mock` no tiene llave, asi que cualquier intento
 * de firmar falla. Y solo existe en desarrollo — `import.meta.env.DEV` es constante al compilar, y
 * esto no viaja al bundle de produccion.
 */
const mirar = import.meta.env.DEV
  ? new URLSearchParams(location.search).get("mirar")
  : null;
const soloMirar = /^0x[0-9a-fA-F]{40}$/.test(mirar ?? "") ? (mirar as `0x${string}`) : null;

/**
 * Conexion de wallet y transportes. Sustituye las 533 lineas de `02-wallet.js`, que hacian a mano
 * el cambio de red, la reconexion, el seguimiento de cuenta y los reintentos.
 *
 * De momento solo `injected` (MetaMask/Rabby y compatibles), que es lo unico que soportaba la app
 * vieja. WalletConnect se añade despues: pide un projectId de su nube y es una decision aparte.
 */
export const config = createConfig({
  chains: [arbitrum, robinhood, hyperevm, bsc, base, ethereum],
  connectors: soloMirar ? [mock({ accounts: [soloMirar] }), injected()] : [injected()],
  transports: {
    // ARBITRUM VA POR EL OFICIAL, Y PUBLICNODE SOLO DE RESPALDO. Estaba al revés y costó una
    // creación de piscina el 2026-08-16: `arbitrum-one.publicnode.com` contesta a
    // `eth_getTransactionReceipt` con «Invalid parameters … Archive requests require a personal
    // token». En Arbitrum los bloques salen cada ~0,25 s, así que una transacción de hace medio
    // minuto ya le parece "de archivo" — justo la que se acaba de firmar y se está esperando.
    //
    // El síntoma es de los malos: la transacción ENTRA (comprobado, `status 1`, el vault se creó)
    // y la app la da por fallida porque no puede leer el recibo. El usuario ve un error rojo sobre
    // algo que sí funcionó y, si lo reintenta, paga dos veces.
    //
    // Ese endpoint ya tenía antecedentes: rechaza `eth_getLogs` desde el bloque 0, y por eso existe
    // `rpcLogs` en networks.ts. Era el mismo endpoint fallando por otra puerta.
    //
    // SIN RESPALDO, Y ES DELIBERADO. El primer intento fue `fallback([oficial, publicnode])`, y no
    // sirve: en cuanto el oficial tropieza una vez, viem pasa al segundo y vuelve EL MISMO error.
    // Un respaldo que falla siempre no es un respaldo, es el fallo esperando su turno.
    //
    // Se probaron siete RPC públicos de Arbitrum (2026-08-16) pidiendo un recibo real con la
    // cabecera `Origin`, que es como lo hace el navegador. Solo uno pasa las dos pruebas:
    //
    //   arb1.arbitrum.io/rpc          CORS si · recibo OK      <- el único válido
    //   arbitrum-one.publicnode.com   CORS si · "Archive requests require a personal token"
    //   arbitrum.drpc.org             CORS si · "Request timeout on the free plan"
    //   1rpc.io/arb                   CORS NO · recibo OK      <- inservible desde el navegador
    //   blockpi / ankr                sin respuesta útil / exige API key
    //
    // Si algún día hace falta redundancia de verdad, el segundo tiene que ser un endpoint de pago
    // con clave. Poner otro gratis es volver aquí.
    // Público DELANTE, Alchemy (por nuestro proxy `/rpc/`, clave del lado servidor) DETRÁS. Es la
    // redundancia de pago que pedía la nota de abajo: si el público limita o cae, `fallback` pasa a
    // Alchemy sin que el usuario vea nada, y el tráfico normal NO consume la cuota de Alchemy.
    // BETA: proxy /rpc/{red}/ (same-origin, sin CORS) montado en el vhost. Robinhood va SOLO por proxy
    // (su RPC bloquea CORS del navegador). Las que traen CORS van directas, con /rpc/ de respaldo.
    [arbitrum.id]: fallback([http("https://arb1.arbitrum.io/rpc"), http("/rpc/arbitrum/")]),
    [robinhood.id]: fallback([http("/rpc/robinhood/")]),
    // El RPC oficial de HyperEVM RECHAZA lotes de mas de 20 llamadas: contesta HTTP 200 con un
    // unico objeto de error sin `id`, y eso tumba TODAS las peticiones del lote con "missing
    // response for request". En la app vieja se veia como pools sin precio, sin simbolos y sin
    // saldos: lo que salia suelto cargaba y lo que iba en paralelo, no.
    [hyperevm.id]: fallback([
      http("/rpc/hyperevm/", { batch: { batchSize: 20 } }),
      http("https://rpc.hyperliquid.xyz/evm", { batch: { batchSize: 20 } }),
    ]),
    // BNB CHAIN: bloXroute DELANTE y nuestro proxy DETRÁS.
    //
    // El proxy `/rpc/bsc/` existe porque apuntar a `bsc-dataseed.bnbchain.org` desde el navegador
    // daba «Failed to fetch» mientras la MISMA llamada funcionaba desde el servidor —CORS correcto,
    // preflight correcto, 30 peticiones seguidas sin un fallo—: es un dominio de Binance y las
    // listas de bloqueo lo traen de serie, así que la petición no llegaba ni a salir.
    //
    // Pero el proxy sale caro: el VPS está en Tokio, así que cada lectura da la vuelta al mundo dos
    // veces. Medido el 2026-08-19 con la misma llamada: **259 ms por el proxy contra 84 ms directo**,
    // y algún pico de 61 s. Una pantalla de esta app son decenas de lecturas, y eso es exactamente
    // lo que hacía que BNB Chain «costara demasiado» comparada con las otras tres redes.
    //
    // bloXroute ya se usa aquí para los eventos, tiene `access-control-allow-origin: *`, no es un
    // dominio de los que se bloquean, y sirve TODO lo que la app necesita (comprobado: recibos,
    // bloques antiguos, balances y `eth_call`). El proxy se queda de respaldo: si bloXroute limita
    // o cae, `fallback` pasa al siguiente sin que el usuario vea nada.
    [bsc.id]: fallback([http("https://bsc.rpc.blxrbdn.com"), http("/rpc/bsc/")]),
    // BASE: NUESTRO PROXY `/rpc/base/` (publicnode server-side) DELANTE, `mainnet.base.org` detras.
    // El orden es DELIBERADO y esta MEDIDO (2026-09-05): base.org RATE-LIMITEA brutalmente las rafagas
    // de lectura del dashboard —en una prueba fallaban 9-11 de 12 lecturas—, y como `piscinasDe` usa
    // `allowFailure:true` y descarta el vault si le falta un campo, esos vaults se caian del TVL EN
    // SILENCIO (el dueño vio "bajar el TVL"). publicnode via el proxy servia 12/12 sin fallar. El proxy
    // es same-origin ('self') -> ni CORS ni CSP. `mainnet.base.org` queda de respaldo. OJO: los EVENTOS
    // (getLogs) NO van por aqui —publicnode pide token de archivo—: van por `rpcLogs`=mainnet.base.org.
    [base.id]: fallback([http("/rpc/base/"), http("https://mainnet.base.org")]),
    // ETHEREUM: publicnode DELANTE (CORS abierto, sin clave) y nuestro proxy DETRÁS. Mainnet es la
    // red con más RPC públicos sanos, así que aquí el problema no es la disponibilidad sino el
    // rate-limit en ráfaga; el proxy es la válvula de escape, igual que en BNB.
    [ethereum.id]: fallback([http("https://ethereum-rpc.publicnode.com"), http("/rpc/ethereum/")]),
  },
});

declare module "wagmi" {
  interface Register {
    config: typeof config;
  }
}
